import React, {
  forwardRef,
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import type { ReportData } from "../types/global";
import {
  getReportColor,
  getReportTitle,
  getReportDate,
} from "../utils/reportProps";
import { getTrashTypeText } from "../utils/reportFormatting";
import "./styles/ReportsListItem.css";
import { thumbUrl } from "../utils/api";
import { useDataContext } from "../contexts/DataContext";

interface ReportsListItemProps {
  report: ReportData;
  onClick: () => void;
  isSelected?: boolean;
}

const PinIcon: React.FC = () => (
  <svg
    className="item-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21s-6-5.686-6-10a6 6 0 1 1 12 0c0 4.314-6 10-6 10z" />
    <circle cx="12" cy="11" r="2.5" />
  </svg>
);

const WasteIcon: React.FC = () => (
  <svg
    className="item-meta-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7h16" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
  </svg>
);

const NoImage: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#E7E4D8" strokeWidth="2" fill="#F7F6EF" />
    <path d="M9 9L15 15M15 9L9 15" stroke="#E7E4D8" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const ReportsListItem = memo(
  forwardRef<HTMLDivElement, ReportsListItemProps>(
    ({ report, onClick, isSelected = false }, ref) => {
      const { t } = useTranslation();
      const { loadVisibleReports } = useDataContext();
      const rootRef = useRef<HTMLDivElement | null>(null);
      // The list only needs the preview tier (thumbnail/date/user). A row needs
      // a load only while it's still minimal; once it has preview (or full)
      // data it's done — no re-fetch, no loading affordance.
      const needsPreview =
        report.dataLevel !== "preview" && report.dataLevel !== "full";

      const statusColor = useMemo(() => getReportColor(report), [report]);
      const statusTitle = useMemo(() => getReportTitle(report), [report]);
      const reportDate = useMemo(() => getReportDate(report), [report]);

      // Lazy-load the row's preview data when it scrolls into view.
      useEffect(() => {
        if (!needsPreview) return;
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
          loadVisibleReports([report._id], true);
          return;
        }
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              loadVisibleReports([report._id], true);
              observer.disconnect();
            }
          },
          { rootMargin: "200px" }
        );
        observer.observe(el);
        return () => observer.disconnect();
      }, [needsPreview, report._id, loadVisibleReports]);

      // Merge the forwarded ref (used for scroll-into-view) with our own.
      const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
          rootRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
          }
        },
        [ref]
      );

      // Location: a human place name when the server has one, else coordinates.
      const locationText = useMemo(() => {
        if (report.locationName && report.locationName.trim()) {
          return report.locationName.trim();
        }
        const { latitude, longitude } = report.geoposition ?? {};
        if (typeof latitude !== "number" || typeof longitude !== "number") {
          return null;
        }
        return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
      }, [report.locationName, report.geoposition]);

      // Severity: bag count (mirrors the mobile app's preference order) + the
      // trash-type text when the full tier has loaded it.
      const amountValue =
        report.cleaned?.amount ??
        (typeof report.amount === "number" ? report.amount : report.amountPreview);
      const amountLabel =
        typeof amountValue === "number" && amountValue > 0
          ? `${amountValue} ${t(amountValue === 1 ? "pollution.bag" : "pollution.bags")}`
          : null;
      const trashLabel = useMemo(
        () => getTrashTypeText(report.trashTypes),
        [report.trashTypes]
      );
      const severityText = [amountLabel, trashLabel].filter(Boolean).join(" · ");

      const cleanerNick =
        report.cleaned?.user?.nickname || report.cleanedUserNickname;
      const distanceLabel =
        typeof report.distance === "number"
          ? `${report.distance.toFixed(1)} km`
          : null;

      // List rows are small, so use the lightweight server thumbnail rather
      // than the full image. The chosen filename is already the representative
      // image (cleaned image when cleaned, else the report image).
      const displayImage = useMemo(() => {
        const previewImg =
          report.images && report.images.length > 0 ? report.images[0] : null;
        const cleanupImg =
          report.cleaned?.images && report.cleaned.images.length > 0
            ? report.cleaned.images[0]
            : null;

        const source = cleanupImg || previewImg;
        return source ? thumbUrl(source) : null;
      }, [report.images, report.cleaned?.images]);

      const handleImageError = useCallback(
        (e: React.SyntheticEvent<HTMLImageElement>) => {
          const target = e.target as HTMLImageElement;
          target.src =
            "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjhGOUZBIi8+CjxwYXRoIGQ9Ik0yMCAyMEwyNSAzMEwzNSAyMEw0MCA0MEgyMFYyMFoiIGZpbGw9IiNERUUyRTYiLz4KPGNpcmNsZSBjeD0iMjgiIGN5PSIyNSIgcj0iMyIgZmlsbD0iI0RFRTJFNiIvPgo8L3N2Zz4K";
          target.style.objectFit = "contain";
        },
        []
      );

      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
        [onClick]
      );

      return (
        <div
          className={`reports-list-item ${isSelected ? "selected" : ""}`}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          ref={setRefs}
          role="button"
          tabIndex={0}
          aria-label={`${statusTitle}${locationText ? `, ${locationText}` : ""}`}
        >
          <span
            className="item-rail"
            style={{ backgroundColor: statusColor }}
            aria-hidden="true"
          />

          <div className="item-main">
            <div className="item-thumb">
              {displayImage ? (
                <img
                  src={displayImage}
                  alt=""
                  loading="lazy"
                  onError={handleImageError}
                />
              ) : (
                <div className="no-image-placeholder">
                  <NoImage />
                </div>
              )}
            </div>

            <div className="item-body">
              <div className="item-row-status">
                <span className="item-status" style={{ color: statusColor }}>
                  {statusTitle}
                </span>
                <span className="item-date">{reportDate}</span>
              </div>

              {severityText && (
                <div className="item-severity">
                  <WasteIcon />
                  <span className="item-severity-text">{severityText}</span>
                </div>
              )}

              <div className="item-actor">
                {cleanerNick ? (
                  <span className="item-actor-cleaned">
                    {t("cleanup.cleanedBy")} @{cleanerNick}
                  </span>
                ) : report.user ? (
                  <span className="item-actor-reporter">
                    @{report.user.nickname}
                  </span>
                ) : null}
                {distanceLabel && (
                  <span className="item-distance">· {distanceLabel}</span>
                )}
              </div>

              {locationText && (
                <div className="item-location">
                  <PinIcon />
                  <span className="item-location-text">{locationText}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  )
);

ReportsListItem.displayName = "ReportsListItem";

export default ReportsListItem;
